import React, { Fragment, useEffect, useRef } from "react"
import { Hole, TeamRound, TeamRoundPlayer } from "../types"
import { formatScore } from "../util"

interface ScorecardProps {
  numberOfHoles: number
  holes: Hole[]
  teamRound: TeamRound
  holeIndex: number
  onScoreCellPress?: (holeIndex: number, player: TeamRoundPlayer) => void
}

export default function Scorecard({
  numberOfHoles,
  holes,
  teamRound,
  holeIndex,
  onScoreCellPress
}: ScorecardProps) {
  const ref = useRef<HTMLDivElement>()
  const { players } = teamRound
  const startIndex = teamRound.startHoleNumber > 9 ? 9 : 0
  const teamHoles = numberOfHoles == 9 ? holes.slice(startIndex, startIndex + 9) : holes

  let totalParFront = 0
  let totalParBack = 0
  let totalPar = 0

  let runningTotalFront = 0
  let runningTotalBack = 0
  let runningTotal = 0

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollLeft = holeIndex >= 9 ? 300 : 0
    }
  }, [holeIndex])

  return (
    <div id={`scorecard-${teamRound.id}`} ref={ref} style={{ marginBottom: "1rem", overflow: "scroll hidden" }}>
      <table className="scorecard">
        <tbody>
          <tr>
            <th className="left"></th>
            {teamHoles.map((hole, i) => {
              return (
                <Fragment key={i}>
                  <th key={hole.number}>{hole.number}</th>
                  {i === 8 && numberOfHoles === 9 ? <th key="out">&nbsp;</th> : null}
                  {i === 8 && numberOfHoles === 18 ? <th key="out">Out</th> : null}
                  {i === 17 ? <th key="in">In</th> : null}
                  {i === 17 ? <th key="total">&nbsp;</th> : null}
                </Fragment>
              )
            })}
          </tr>
          <tr>
            <td className="left">Par</td>
            {teamHoles.slice(0, 9).map((hole, i) => {
              totalParFront = totalParFront + hole.par
              totalPar = totalPar + hole.par
              return (
                <td className="score" key={i}>
                  {hole.par}
                </td>
              )
            })}
            <td className="score">{totalParFront}</td>
            {numberOfHoles === 18 ? (
              <>
                {teamHoles.slice(9, 18).map((hole, i) => {
                  totalParBack = totalParBack + hole.par
                  totalPar = totalPar + hole.par
                  return (
                    <td className="score" key={i}>
                      {hole.par}
                    </td>
                  )
                })}
                <td className="score">{totalParBack}</td>
                <td className="score">{totalPar}</td>
              </>
            ) : (
              <></>
            )}
          </tr>
          {players
            .filter((p) => !p.canceled)
            .map((p, i) => {
              return (
                <tr key={i}>
                  <td className="left">{p.user.abbreviatedName}</td>
                  {teamHoles.slice(0, 9).map((hole, index) => {
                    return (
                      <HoleScoreCell
                        key={hole.id}
                        player={p}
                        hole={hole}
                        holeIndex={index}
                        onPress={onScoreCellPress}
                      />
                    )
                  })}
                  <td>{sumOfGrossScores(teamHoles.slice(0, 9), p)}</td>
                  {numberOfHoles === 18 ? (
                    <>
                      {teamHoles.slice(9, 18).map((hole, index) => {
                        return (
                          <HoleScoreCell
                            key={hole.id}
                            player={p}
                            hole={hole}
                            holeIndex={index + 9}
                            onPress={onScoreCellPress}
                          />
                        )
                      })}
                      <td>{sumOfGrossScores(teamHoles.slice(9, 18), p)}</td>
                      <td>{sumOfGrossScores(teamHoles.slice(0, 18), p)}</td>
                    </>
                  ) : (
                    <></>
                  )}
                </tr>
              )
            })}
          <tr>
            <td className="left">Net</td>
            {teamHoles.slice(0, 9).map((hole, i) => {
              const score = teamRound.scores?.get(hole.id)?.netScoreRelativeToPar
              if (score) {
                runningTotalFront = runningTotalFront + score
                runningTotal = runningTotal + score
              }
              return (
                <td className="score" key={i}>
                  {score || score === 0 ? formatScore(score) : ""}
                </td>
              )
            })}
            <td style={{ fontWeight: "bold" }}>{formatScore(runningTotalFront)}</td>
            {numberOfHoles === 18 ? (
              <>
                {teamHoles.slice(9, 18).map((hole, i) => {
                  const score = teamRound.scores?.get(hole.id)?.netScoreRelativeToPar
                  if (score) {
                    runningTotalBack = runningTotalBack + score
                    runningTotal = runningTotal + score
                  }
                  return (
                    <td className="score" key={i}>
                      {score || score === 0 ? formatScore(score) : ""}
                    </td>
                  )
                })}
                <td style={{ fontWeight: "bold" }}>{formatScore(runningTotalBack)}</td>
                <td style={{ fontWeight: "bold" }}>{formatScore(runningTotal)}</td>
              </>
            ) : (
              <></>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

interface HoleScoreCellProps {
  player: TeamRoundPlayer
  hole: Hole
  holeIndex: number
  onPress?: (holeIndex: number, player: TeamRoundPlayer) => void
}

function HoleScoreCell({ player, hole, holeIndex, onPress }: HoleScoreCellProps) {
  const score = player.scores?.get(hole.id)
  const tappable = !!onPress
  const handleClick = tappable
    ? () => {
        onPress(holeIndex, player)
      }
    : undefined

  if (!score) {
    return (
      <td
        className={tappable ? "score score--tappable" : undefined}
        role={tappable ? "button" : undefined}
        onClick={handleClick}
      />
    )
  }

  const value =
    score.netScore < score.grossScore ? (
      <span>
        <sup>{score.grossScore}</sup>&frasl;<sub>{score.netScore}</sub>
      </span>
    ) : (
      <span>{score.grossScore}</span>
    )

  const className = ["score", tappable ? "score--tappable" : null, score.usedForTeamScore ? "spark-orange-border" : null]
    .filter(Boolean)
    .join(" ")

  return (
    <td className={className} role={tappable ? "button" : undefined} onClick={handleClick}>
      {score.usedForTeamScore ? <strong className="spark-orange">{value}</strong> : value}
    </td>
  )
}

function sumOfGrossScores(holes: Hole[], player: TeamRoundPlayer) {
  let score = 0
  for (let hole of holes) {
    score = score + (player.scores?.get(hole.id)?.grossScore || 0)
  }
  return score || null
}
